/**
 * Repository factory. The whole UI gets its data through `useRepos()`.
 *
 * To add the backend later, add `repositories/supabase/index.ts` exporting
 * `createSupabaseRepositories()` with the same `Repositories` shape and switch
 * on `config.backend` below. Nothing in `features/` or `components/` changes.
 * See docs/BACKEND_INTEGRATION.md.
 */
import { createContext, useContext, useEffect, useState } from 'react';
import { config } from '../config';
import { createMockRepositories } from './mock';
import type { Repositories } from './types';

let singleton: Repositories | null = null;

export function getRepositories(): Repositories {
  if (!singleton) {
    switch (config.backend) {
      case 'mock':
      default:
        singleton = createMockRepositories();
        break;
      // case 'supabase':
      //   singleton = createSupabaseRepositories();
      //   break;
    }
  }
  return singleton;
}

export const ReposContext = createContext<Repositories | null>(null);

export function useRepos(): Repositories {
  const r = useContext(ReposContext);
  if (!r) throw new Error('ReposContext missing');
  return r;
}

/**
 * A number that goes up whenever one of these tables changes — for a screen
 * that keeps something of its own beside what AppState holds (one story's
 * recordings and suggestions, your own unfinished drafts) and has to know when
 * to fetch it again.
 *
 * Such screens used to watch the identity of one of AppState's lists, because
 * every change anywhere replaced every list. Now a list that did not change is
 * the same array it was (see the note on SLICES in AppState), so "the stories
 * list is a new array" no longer means "something about this story changed" —
 * and the honest question was always "did one of *my* tables change?", which is
 * what this asks. Pass a constant array: the subscription is keyed on its words.
 */
export function useTablesVersion(tables: readonly string[]): number {
  const repos = useRepos();
  const [version, setVersion] = useState(0);
  const key = tables.join('|');
  useEffect(
    () =>
      repos.sync.onChange((changed) => {
        if (changed.includes('*') || changed.some((t) => key.split('|').includes(t))) setVersion((v) => v + 1);
      }),
    [repos, key],
  );
  return version;
}

export type { Repositories } from './types';
